import { useCallback } from 'react';

import { ContactProps } from '@/models/Contact';
import { PartnerProps } from '@/models/Partner';

import { ModalTab } from '../models/modalTabs';

const partnerInfoFieldsToUpdate: Array<keyof PartnerProps> = [
  'partnerName',
  'city',
  'stateId',
  'countyId',
  'countryId',
  'notes',
  'specialInstructions',
  'additionalStickers',
];

type SubmitFn = (args: {
  partnerInfoLocal: PartnerProps;
  shippingContact: ContactProps;
  fieldsToUpdate?: Array<keyof PartnerProps>;
  onUpdatedPartner: (updatedPartner: PartnerProps) => void;
  onClose?: () => void;
  onMissingShippingFields?: () => void;
}) => Promise<void>;

type Args = {
  submit: SubmitFn;
  partnerInfoLocal: PartnerProps;
  shippingContact: ContactProps;
  isPartnerNameMissing: boolean;
  setShowAlert: (show: boolean) => void;
  setAlert: (a: { message: string; severity: 'warning' | 'success' | 'error' }) => void;
  setSavingTab: (tab: ModalTab | null) => void;
  onUpdatedPartner: (updatedPartner: PartnerProps) => void;
  markTabClean: (tab: ModalTab) => void;
  markTabSaved: (tab: ModalTab) => void;
  markTabError: (tab: ModalTab) => void;
};

export function usePartnerInfoTabSave({
  submit,
  partnerInfoLocal,
  shippingContact,
  isPartnerNameMissing,
  setShowAlert,
  setAlert,
  setSavingTab,
  onUpdatedPartner,
  markTabClean,
  markTabSaved,
  markTabError,
}: Args) {
  return useCallback(async () => {
    setShowAlert(false);

    if (isPartnerNameMissing) {
      markTabError('partner');
      setAlert({
        message: 'Partner Name is required',
        severity: 'warning',
      });
      return;
    }

    setSavingTab('partner');

    try {
      await submit({
        partnerInfoLocal,
        shippingContact,
        fieldsToUpdate: partnerInfoFieldsToUpdate,
        onUpdatedPartner,
      });

      markTabClean('partner');
      markTabSaved('partner');
    } catch {
      markTabError('partner');
    } finally {
      setSavingTab(null);
    }
  }, [
    isPartnerNameMissing,
    markTabClean,
    markTabError,
    markTabSaved,
    onUpdatedPartner,
    partnerInfoLocal,
    setAlert,
    setSavingTab,
    setShowAlert,
    shippingContact,
    submit,
  ]);
}
